// Brand Console GROWTH -- Action Plans.
//
// AF UX audit P1D (final) slice (docs/product/intelligence-console-ux-audit.md,
// Section 9) -- this page is migrated to the shared IntelligenceCard +
// LineageStrip pattern already proven on Recommendations (P0), Signals
// (P1), Diagnoses (P1B), and Opportunities (P1C). See
// docs/product/martian-intelligence-console-p1d-action-plans-report.md
// for the full report, including live re-verification of the Northfield
// values (re-checked fresh for this slice, not assumed from an earlier
// audit) and the field-mapping decisions noted inline below.
//
// Reuses the existing, already-real AF-2.6 ActionPlanService entirely
// through its existing GET /intelligence/{workspace_id}/action-plans
// route (actionPlansAPI.listForWorkspace) -- no new backend endpoint, no
// new service method, no new collection, no new query param.
//
// Every field this page renders comes directly from ActionPlanService's
// own deterministic readiness/confidence computation (services.py,
// verified by direct read): readiness/readiness_score/confidence/risk are
// pure functions of real, already-recorded asset/data/permission
// availability -- never invented, never a fabricated percentage. Status,
// readiness, approval, and confidence are FOUR SEPARATE real dimensions
// and are rendered as four separate elements (three distinct badges plus
// one independent numeric indicator) -- none is ever inferred from
// another. "Recommendation" is the plan's own real recommendation_id,
// resolved against the workspace's recommendations list -- unchanged from
// before this migration.
//
// Field-mapping note: `action` (the plan's real, full description) was
// already used by this page -- in the pre-migration Sheet, just not in
// the pre-migration table row -- and is exactly the field this task's own
// instructions name as the preferred primary statement ("the real
// action / recommendation field already used by the current page"), so
// it is promoted to the card's primary statement here. `missing_requirements`
// is rendered as an explicit, neutral "What's missing before this is
// ready" list, verbatim, never converted to checkmarks and never implying
// completion. Real backend fields `prerequisites`/`required_assets`/
// `required_data`/`required_permissions`/`rollback_plan`/`validation_steps`
// exist on the ActionPlanService document (confirmed by direct backend
// read) but are not returned to/rendered by this page's Sheet either
// before or after this migration -- this slice does not expand the Sheet
// (per its own explicit instruction); see the P1D report for the full
// list of hidden-but-real fields worth a future information-architecture
// pass.
//
// This page has never had an Approve/Cancel control of its own (that
// write surface lives in RecommendationBridgePanel.jsx, untouched by this
// task) -- nothing here newly adds one.
//
// P1.7: client resolution is still the shared useWorkspaceSelection()
// hook (unchanged from before this migration).

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { IntelligenceCard } from '@/components/brand/IntelligenceCard';
import { LineageStrip } from '@/components/brand/LineageStrip';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { StatusBadge, ConfidenceIndicator, ReadinessBadge, ApprovalBadge } from '@/components/brand/Badges';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { WorkspaceSelect } from '@/components/brand/BrandContextBar';
import { useWorkspaceSelection } from '@/hooks/useWorkspaceSelection';
import { actionPlansAPI, entitiesAPI, recommendationsAPI } from '@/lib/intelligenceApi';

function dash(v) {
  return v === null || v === undefined || v === '' ? '—' : v;
}
function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}
function sortRows(rows, key, dir) {
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av === bv) return 0;
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    const cmp = av < bv ? -1 : 1;
    return dir === 'asc' ? cmp : -cmp;
  });
  return copy;
}
// Same sort mechanism the old <th> column headers triggered -- only the
// trigger control changed (a labeled button instead of a table header),
// since a card list has no header row to click. Established in the
// Diagnoses (P1B) migration.
function SortButton({ label, col, sort, onSort, testId }) {
  const active = sort.key === col;
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      data-testid={testId}
      className={`h-8 inline-flex items-center gap-1 rounded-[var(--radius-button,6px)] border border-[var(--border)] px-2.5 text-[11px] font-medium ${active ? 'bg-[var(--surface-sunken,var(--canvas-secondary))] text-[var(--text-primary)]' : 'text-[var(--text-secondary)] hover:bg-[var(--neutral-bg)]'}`}
    >
      Sort: {label}{active ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </button>
  );
}

export default function ActionPlansPage() {
  // P1.7: client resolution is now the shared useWorkspaceSelection() hook
  // (see SignalsPage.js's own comment for the full rationale) instead of
  // an unswitchable first-client fetch.
  const { clients, workspaceId, setWorkspaceId, loadingClients } = useWorkspaceSelection();
  const navigate = useNavigate();
  const clientId = workspaceId || null;
  const noClient = !loadingClients && clients.length === 0;
  const [plans, setPlans] = useState(null); // null = loading
  const [entityNames, setEntityNames] = useState({});
  const [recommendationById, setRecommendationById] = useState({});
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({ status: '', readiness: '' });
  const [appliedFilters, setAppliedFilters] = useState({ status: '', readiness: '' });

  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [selected, setSelected] = useState(null);

  const load = useCallback((activeFilters) => {
    if (!clientId) return;
    setPlans(null);
    setError(null);
    const params = {};
    if (activeFilters.status) params.status = activeFilters.status;
    if (activeFilters.readiness) params.readiness = activeFilters.readiness;
    Promise.all([
      actionPlansAPI.listForWorkspace(clientId, params),
      entitiesAPI.list(clientId, { includeArchived: true }).catch(() => ({ data: { entities: [] } })),
      recommendationsAPI.listForWorkspace(clientId).catch(() => ({ data: { recommendations: [] } })),
    ])
      .then(([planRes, entRes, recRes]) => {
        setPlans(planRes.data?.action_plans || []);
        const names = {};
        (entRes.data?.entities || []).forEach((e) => { names[e.id] = e.canonical_name || e.id; });
        setEntityNames(names);
        const recs = {};
        (recRes.data?.recommendations || []).forEach((r) => { recs[r.id] = r; });
        setRecommendationById(recs);
      })
      .catch((e) => setError(e));
  }, [clientId]);

  useEffect(() => { load(appliedFilters); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
    load(filters);
  };
  const clearFilters = () => {
    const cleared = { status: '', readiness: '' };
    setFilters(cleared);
    setAppliedFilters(cleared);
    load(cleared);
  };

  const sortedRows = useMemo(() => (plans ? sortRows(plans, sort.key, sort.dir) : []), [plans, sort]);
  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));
  };

  const recLabel = (id) => (id && recommendationById[id]?.title) || id || null;

  return (
    <DashboardLayout>
      <div data-testid="action-plans-page" className="max-w-5xl mx-auto">
        <div className="mb-6">
          <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--accent)] mb-1">What does it take to execute?</p>
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Action Plans</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">Real readiness, approval, and governance state Martian has computed for every action plan — never a fabricated percentage.</p>
        </div>

        {!noClient && clients.length > 0 && (
          <div className="mb-4">
            <WorkspaceSelect clients={clients} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
          </div>
        )}

        {noClient && <EmptyState title="ADD A BRAND FIRST" description="Action plans appear once Martian has a workspace with detected recommendations." />}

        {!noClient && plans === null && !error && <LoadingState />}
        {!noClient && error && <ErrorState error={error} onRetry={() => load(appliedFilters)} />}

        {!noClient && !error && plans !== null && (
          <>
            <form onSubmit={applyFilters} className="flex items-end gap-2 flex-wrap mb-4" data-testid="action-plans-filters">
              <div>
                <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Status</label>
                <Select value={filters.status || 'ANY'} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === 'ANY' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-plan-status" className="h-8 w-[150px] text-[12px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ANY">Any</SelectItem>
                    {['DRAFT', 'READY', 'BLOCKED', 'APPROVED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'].map((s) => (
                      <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Readiness</label>
                <Select value={filters.readiness || 'ANY'} onValueChange={(v) => setFilters((f) => ({ ...f, readiness: v === 'ANY' ? '' : v }))}>
                  <SelectTrigger data-testid="filter-plan-readiness" className="h-8 w-[150px] text-[12px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ANY">Any</SelectItem>
                    {['READY', 'PARTIALLY_READY', 'NOT_READY', 'BLOCKED'].map((s) => (
                      <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" size="sm" data-testid="apply-plan-filters" className="h-8 text-[12px]">Apply</Button>
              <Button type="button" variant="outline" size="sm" data-testid="clear-plan-filters" className="h-8 text-[12px]" onClick={clearFilters}>Clear filters</Button>
              <div className="flex items-center gap-2 ml-auto">
                <SortButton label="Status" col="status" sort={sort} onSort={toggleSort} testId="sort-plan-status" />
                <SortButton label="Confidence" col="confidence" sort={sort} onSort={toggleSort} testId="sort-plan-confidence" />
                <SortButton label="Created" col="created_at" sort={sort} onSort={toggleSort} testId="sort-plan-created" />
              </div>
            </form>

            {plans.length === 0 && (
              <EmptyState
                testId="action-plans-empty"
                title="No action plans yet"
                description="Martian generates an action plan only after a recommendation has been detected -- none exist for this workspace yet matching these filters."
              />
            )}

            {plans.length > 0 && (
              <div className="space-y-4" data-testid="action-plans-list">
                {sortedRows.map((p) => {
                  const readinessPct = p.readiness_score === null || p.readiness_score === undefined ? null : Math.round(p.readiness_score * 100);
                  const missing = Array.isArray(p.missing_requirements) ? p.missing_requirements : [];
                  // P1 console polish (docs/product/intelligence-console-whole-chain-audit.md,
                  // P1 #2) -- clickable only when the recommendation
                  // actually resolved against the real, already-fetched
                  // recommendationById map (a genuine record, not merely
                  // a dangling id). Navigates to the Recommendations page
                  // itself, same non-deep-linked pattern as the
                  // pre-existing Signals -> Opportunity link.
                  const lineageItems = [];
                  if (p.recommendation_id) {
                    const resolvedRecommendation = recommendationById[p.recommendation_id];
                    lineageItems.push({
                      label: 'From recommendation',
                      value: recLabel(p.recommendation_id),
                      testId: 'lineage-recommendation',
                      onClick: resolvedRecommendation ? () => navigate('/growth/recommendations') : undefined,
                    });
                  }

                  return (
                    <IntelligenceCard
                      key={p.id}
                      testId={`action-plan-card-${p.id}`}
                      stage="ACTION PLAN"
                      title={dash(p.title)}
                      badges={(
                        <>
                          {p.status && <StatusBadge value={p.status} />}
                          {p.readiness && <ReadinessBadge value={p.readiness} />}
                          {p.approval_status && <ApprovalBadge value={p.approval_status} />}
                        </>
                      )}
                      statementLabel="Action"
                      statement={p.action}
                      metrics={[
                        readinessPct !== null ? { label: 'Readiness score', value: `${readinessPct}%` } : null,
                        p.confidence !== undefined && p.confidence !== null
                          ? { label: 'Confidence', value: <ConfidenceIndicator value={p.confidence} />, testId: 'intelligence-card-confidence' }
                          : null,
                        p.risk ? { label: 'Risk', value: p.risk } : null,
                        p.effort ? { label: 'Effort', value: p.effort } : null,
                        p.reversibility ? { label: 'Reversibility', value: p.reversibility } : null,
                      ].filter(Boolean)}
                      lineage={(
                        <>
                          {missing.length > 0 && (
                            <div
                              className="mb-3 px-3 py-2 rounded border border-[var(--border)] bg-[var(--surface-sunken,var(--canvas-secondary))]"
                              data-testid="action-plan-missing-requirements"
                            >
                              <p className="text-[9px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-1">What's missing before this is ready</p>
                              <ul className="list-disc list-inside text-[11px] text-[var(--text-secondary)]">
                                {missing.map((m, i) => <li key={i}>{typeof m === 'string' ? m : JSON.stringify(m)}</li>)}
                              </ul>
                            </div>
                          )}
                          <LineageStrip items={lineageItems} />
                        </>
                      )}
                      decision={(
                        <button
                          type="button"
                          data-testid="action-plan-view-details"
                          onClick={() => setSelected(p)}
                          className="inline-flex items-center gap-1 rounded-[var(--radius-button,6px)] border border-[var(--border)] bg-[var(--surface-sunken,var(--canvas-secondary))] px-2.5 py-1 text-[11px] font-medium text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]"
                        >
                          View full action plan →
                        </button>
                      )}
                    />
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── Action plan detail -- unchanged from the pre-migration table:
            same Sheet, same fields, same open/close behavior, now reached
            via each card's explicit "View full action plan" button instead
            of an implicit whole-row click. ── */}
        <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-[var(--surface)] border-[var(--border)]" data-testid="action-plan-detail">
            {selected && (
              <>
                <SheetHeader>
                  <SheetTitle className="text-[var(--text-primary)] text-[14px]">{dash(selected.title)}</SheetTitle>
                  <SheetDescription className="text-[var(--text-tertiary)] text-[11px]">The real, stored action plan record.</SheetDescription>
                </SheetHeader>
                <div className="mt-3 space-y-3 text-[12px]">
                  <div>
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Action</p>
                    <p className="text-[var(--text-primary)] whitespace-pre-wrap">{dash(selected.action)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Objective</p>
                    <p className="text-[var(--text-primary)]">{dash(selected.objective)}</p>
                  </div>
                  <Separator className="bg-[var(--border)]" />
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Entity</p><p className="text-[var(--text-primary)]">{dash(entityNames[selected.entity_id] || selected.entity_id)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Type</p><p className="text-[var(--text-primary)] font-mono">{dash(selected.action_plan_type)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Status</p><p>{selected.status ? <StatusBadge value={selected.status} /> : '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Readiness</p><p>{selected.readiness ? <ReadinessBadge value={selected.readiness} /> : '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Readiness score</p><p className="text-[var(--text-primary)]">{selected.readiness_score === null || selected.readiness_score === undefined ? '—' : `${Math.round(selected.readiness_score * 100)}%`}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Approval</p><p>{selected.approval_status ? <ApprovalBadge value={selected.approval_status} /> : '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Confidence</p><ConfidenceIndicator value={selected.confidence} /></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Risk</p><p>{selected.risk ? <StatusBadge value={selected.risk} /> : '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Effort</p><p className="text-[var(--text-primary)]">{dash(selected.effort)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Reversibility</p><p className="text-[var(--text-primary)]">{dash(selected.reversibility)}</p></div>
                  </div>
                  <Separator className="bg-[var(--border)]" />
                  <div>
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Recommendation</p>
                    <p className="text-[var(--text-primary)]" data-testid="detail-plan-recommendation">{dash(recLabel(selected.recommendation_id))}</p>
                  </div>
                  {selected.success_metrics && (selected.success_metrics.primary_metric || selected.success_metrics.target_direction) && (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Primary metric</p><p className="text-[var(--text-primary)] font-mono">{dash(selected.success_metrics.primary_metric)}</p></div>
                      <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Target direction</p><p className="text-[var(--text-primary)]">{dash(selected.success_metrics.target_direction)}</p></div>
                    </div>
                  )}
                  {Array.isArray(selected.guardrail_metrics) && selected.guardrail_metrics.length > 0 && (
                    <>
                      <Separator className="bg-[var(--border)]" />
                      <div>
                        <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-1">Guardrail metrics</p>
                        <div className="space-y-1" data-testid="detail-plan-guardrails">
                          {selected.guardrail_metrics.map((g, i) => (
                            <div key={i} className="flex items-center justify-between text-[11px] bg-[var(--surface-sunken,var(--canvas-secondary))] rounded px-2 py-1">
                              <span className="text-[var(--text-primary)] font-mono">{dash(g.metric)}</span>
                              <span className="text-[var(--text-tertiary)]">{dash(g.direction)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  {Array.isArray(selected.missing_requirements) && selected.missing_requirements.length > 0 && (
                    <>
                      <Separator className="bg-[var(--border)]" />
                      <div>
                        <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-1">Missing requirements</p>
                        <ul className="list-disc list-inside text-[11px] text-[var(--text-secondary)]" data-testid="detail-plan-missing-requirements">
                          {selected.missing_requirements.map((m, i) => <li key={i}>{typeof m === 'string' ? m : JSON.stringify(m)}</li>)}
                        </ul>
                      </div>
                    </>
                  )}
                  <Separator className="bg-[var(--border)]" />
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Created</p><p className="text-[var(--text-primary)]">{formatDateTime(selected.created_at)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Approved</p><p className="text-[var(--text-primary)]">{formatDateTime(selected.approved_at)}</p></div>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </DashboardLayout>
  );
}
